const GRADE_GROUPS = [
  {
    badge: 'Grades 1–5',
    badgeColor: 'text-orange border-orange-300',
    badgeBg: 'bg-orange-100',
    cardBorder: 'border-orange-300',
    title: 'These are your youngest learners, curious and full of energy.',
    description:
      'They thrive with play, visuals, and hands-on activities. Patience, encouragement, and fun lessons are key. Celebrate every small win—each "I did it!" moment builds confidence. For these students, you are making their first experiences with learning exciting and positive.',
  },
  {
    badge: 'Grades 6–10',
    badgeColor: 'text-blue-200 border-blue-200',
    badgeBg: 'bg-blue-50',
    cardBorder: 'border-blue-200',
    title: 'Your pre-teen and early teen students are eager to be challenged.',
    description:
      "They want to build, solve problems, and show what they're capable of. Provide projects they can take pride in, ask thought-provoking questions, and allow them to experiment, even fail; because that's where real growth happens. For these students, you are fueling curiosity and helping them discover they can do hard things.",
  },
  {
    badge: 'Grades 11–12',
    badgeColor: 'text-orange2-400 border-orange2-400',
    badgeBg: 'bg-orange2-50',
    cardBorder: 'border-orange2-400',
    title:
      'These teen learners are ready for deeper challenges and independent thinking.',
    description:
      'They want to tackle meaningful projects, solve complex problems, and see the impact of their work. Encourage experimentation, critical thinking, and reflection. For these students, you are guiding them to refine their skills, build confidence, and prepare for the next stage of learning.',
  },
]

const DashedConnector = ({ vertical = false }: { vertical?: boolean }) =>
  vertical ? (
    <div className="flex justify-center py-1">
      <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
        <path
          d="M20 0 C 10 12, 30 24, 20 48"
          stroke="#C1C6CE"
          strokeWidth="2"
          strokeDasharray="5 4"
          fill="none"
        />
      </svg>
    </div>
  ) : (
    <div className="flex items-center pl-1 flex-shrink-0">
      <svg width="60" height="40" viewBox="0 0 60 40" fill="none">
        <path
          d="M0 20 C 15 5, 45 35, 60 20"
          stroke="#C1C6CE"
          strokeWidth="2"
          strokeDasharray="5 4"
          fill="none"
        />
      </svg>
    </div>
  )

const WhoYouTeach = () => (
  <div className="px-5 py-12 lg:py-16 bg-grey-50">
    <div className="container mx-auto">
      {/* Header */}
      <div className="text-center mb-10 max-w-4xl mx-auto">
        <h2 className="pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
          Who you will teach at Codingal
        </h2>
        <p className="text-lg text-grey mx-auto leading-tight">
          You will teach students from Grades 1 through 12, children and teens
          aged 6 to 17. Whether you are leading a one on one session or a small
          group of 2 to 5 learners, here is what to expect.
        </p>
      </div>

      {/* Desktop layout */}
      <div className="hidden lg:flex justify-center gap-0">
        {GRADE_GROUPS.map((group, index) => (
          <div key={group.badge} className="flex">
            {/* Card */}
            <div className="relative flex flex-col items-center text-center max-w-xs h-full">
              {/* Badge */}
              <h3
                className={`${group.badgeBg} ${group.badgeColor} border rounded-xl px-4 py-2 shadow-md text-sm font-600 z-10 -mb-5`}
              >
                {group.badge}
              </h3>
              {/* Card body */}
              <div
                className={`bg-white ${group.cardBorder} border rounded-xl p-4 lg:p-6 flex flex-col gap-3 text-center h-full`}
              >
                <h4 className="text-lg font-600 text-grey-900">
                  {group.title}
                </h4>
                <p className="text-sm text-grey-800 leading-relaxed">
                  {group.description}
                </p>
              </div>
            </div>

            {/* Connector + mascot after last card */}
            {index < GRADE_GROUPS.length - 1 && (
              <DashedConnector vertical={false} />
            )}
          </div>
        ))}
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden flex flex-col items-center">
        {GRADE_GROUPS.map((group, index) => (
          <div key={group.badge} className="flex flex-col items-center w-full">
            {/* Badge */}
            <h3
              className={`${group.badgeBg} ${group.badgeColor} border rounded-full px-4 py-1 text-sm font-600 mb-3 z-10`}
            >
              {group.badge}
            </h3>
            {/* Card */}
            <div
              className={`bg-white ${group.cardBorder} border rounded-xl p-6 flex flex-col gap-3 text-center w-full`}
            >
              <h4 className="text-lg font-600 text-grey-900">{group.title}</h4>
              <p className="text-sm text-grey-800 leading-relaxed">
                {group.description}
              </p>
            </div>
            {/* Connector or mascot */}
            {index < GRADE_GROUPS.length - 1 && (
              <DashedConnector vertical={true} />
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
)

export default WhoYouTeach
